import React, { useMemo, useState } from 'react';
import { useMutationObserver } from '../../hooks/useMutationObserver';
import ProxyButton from './ProxyButton';
import { useEventBus } from '../../hooks/useEventBus';
import withEventHelper from './EventHelper';
import { ButtonVariant } from '@athena/forge';
import Registration from '../primary-auth/Registration';

export interface ButtonBarProps {
  oktaButtonBar: JQuery<HTMLElement>;
  fullWidth?: boolean;
  containerClassName?: string;
  primaryButtonClassName?: string;
  primaryButtonOnClick?: (buttonText: string) => void;
  secondaryButtonOnClick?: (buttonText: string) => void;
  tertiaryButtonOnClick?: (buttonText: string) => void;
  suppressOktaClick?: boolean;
  primaryButtonLabel?: string;
  allowRegistration: boolean;
}

function ButtonBar({
  oktaButtonBar,
  fullWidth = true,
  primaryButtonOnClick,
  containerClassName = '',
  primaryButtonClassName = '',
  secondaryButtonOnClick,
  suppressOktaClick,
  tertiaryButtonOnClick,
  primaryButtonLabel,
  allowRegistration,
}: Readonly<ButtonBarProps>) {
  const firstButton = oktaButtonBar.find('input.button').eq(0);
  const secondButton = oktaButtonBar.find('input.button').eq(1);
  const linkButton = oktaButtonBar.find('a.link').eq(0);
  const [ hidden, setHidden ] = useState(oktaButtonBar[0]?.style?.display === 'none');
  const [ isFormValid, setFormValidState ] = useState(true);
  const justifyContentClass = fullWidth
    ? 'fe_u_flex-justify-content--space-between'
    : 'fe_u_flex-justify-content--center';

  const [ displayPrimaryButton, setDisplayPrimaryButton ] = useState(true);
  const [ primaryButtonVariant, setPrimaryButtonVariant ] = useState<ButtonVariant>('primary');

  useEventBus({
    eventName: 'display-primary-button',
    onEvent: ({ display }) => {
      setDisplayPrimaryButton(display);
    },
  });

  useEventBus({
    eventName: 'convert-primary-button-to-secondary',
    onEvent: () => {
      setPrimaryButtonVariant('secondary');
    },
  });

  useEventBus({
    eventName: 'set-form-validation-state',
    onEvent: ({ isFormValid }) => {
      setFormValidState(isFormValid);
    },
  });

  //useMemo to ensure we don't create new references for the dependencies of useMutationObserver
  const onMutationArgs = useMemo(() => {
    return {
      onMutation: (mutation: MutationRecord) => {
        const buttonBar = mutation.target as HTMLElement;
        setHidden(buttonBar.style.display === 'none');
      },
      filterOptions: { attributeFilter: [ 'style' ] },
    };
  }, []);
  useMutationObserver({
    element: oktaButtonBar[0],
    ...onMutationArgs,
  });

  function getButtons() {
    const buttons = [];
    if (displayPrimaryButton && firstButton.length) {
      buttons.push(
        <ProxyButton
          oktaButton={firstButton}
          key={'first'}
          variant={primaryButtonVariant}
          className={primaryButtonClassName}
          fullWidth={fullWidth}
          disabled={!isFormValid}
          size="large"
          onClick={() => {
            primaryButtonOnClick?.(firstButton.text() || (firstButton.val() as string));
          }}
          suppressOktaClick={suppressOktaClick}
          textToOverride={primaryButtonLabel}
        />
      );
    }
    if (secondButton.length) {
      buttons.push(
        <ProxyButton
          className="fe_u_margin--left-medium fe_c_button--large"
          oktaButton={secondButton}
          key={'second'}
          variant="secondary"
          fullWidth={fullWidth}
          disabled={!isFormValid}
          size="large"
          onClick={() => {
            secondaryButtonOnClick?.(secondButton.text() || (secondButton.val() as string));
          }}
        />
      );
    }
    if (buttons.length) {
      return (
        <>
          <div className={`fe_u_flex-container ${justifyContentClass} ${containerClassName} athena-button-bar`}>
            {buttons}
          </div>
        </>
      );
    }
    else {
      return <></>;
    }
  }
  if (!hidden) {
    return (
      <>
        {getButtons()}
        <ProxyButton
          className="fe_u_margin--top-medium fe_c_button--medium"
          oktaButton={linkButton}
          key={'link'}
          variant="tertiary"
          fullWidth={fullWidth}
          size="medium"
          onClick={() => {
            tertiaryButtonOnClick?.(linkButton.text() || (linkButton.val() as string));
          }}
        />
        {allowRegistration && <Registration />}
      </>
    );
  }
  else {
    return <></>;
  }
}

ButtonBar.displayName = 'ButtonBar';
export default withEventHelper(ButtonBar);
